import { useCallback, useEffect, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { Inbox, MessageSquareReply, RefreshCw } from "lucide-react";
import {
  Badge,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@alight/ui";
import { adminListMessages, ApiError, type Message, type MessageStatus } from "../../lib/api";
import { formatDay, TopicBadge } from "../../components/admin-bits";

export const Route = createFileRoute("/admin/")({
  component: AdminInbox,
});

const TABS: Array<{ key: MessageStatus; label: string }> = [
  { key: "new", label: "New" },
  { key: "replied", label: "Replied" },
  { key: "archived", label: "Archived" },
];

const EMPTY_COPY: Record<MessageStatus, string> = {
  new: "All caught up — no new messages waiting.",
  replied: "No replied messages yet. Answers you send will collect here.",
  archived: "Nothing archived. Archived messages are kept here for reference.",
};

function AdminInbox() {
  const navigate = useNavigate();
  const [tab, setTab] = useState<MessageStatus>("new");
  const [messages, setMessages] = useState<Message[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (status: MessageStatus) => {
    setLoading(true);
    setError(null);
    try {
      setMessages(await adminListMessages(status));
    } catch (err) {
      setMessages(null);
      setError(err instanceof ApiError ? err.message : "Couldn't load messages.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load(tab);
  }, [tab, load]);

  // Auto-refresh when the operator comes back to the tab.
  useEffect(() => {
    const onFocus = () => void load(tab);
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [tab, load]);

  const rows = messages ?? [];

  return (
    <section className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold text-ink">Inbox</h1>
          <p className="mt-1 text-sm text-muted">
            Expressions of interest, enquiries and feedback from the website form.
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => void load(tab)}
          disabled={loading}
        >
          <RefreshCw className={loading ? "h-4 w-4 animate-spin" : "h-4 w-4"} aria-hidden />
          Refresh
        </Button>
      </header>

      <div role="tablist" aria-label="Message status" className="flex gap-2 border-b border-border">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={tab === t.key}
            onClick={() => setTab(t.key)}
            className={
              "-mb-px border-b-2 px-3 pb-2 text-sm font-semibold transition-colors " +
              (tab === t.key
                ? "border-primary text-primary"
                : "border-transparent text-muted hover:text-ink")
            }
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading && (
        <div className="rounded-card border border-border bg-card px-4 py-10 text-center text-sm text-muted">
          Loading messages…
        </div>
      )}

      {!loading && error && (
        <div
          role="alert"
          className="rounded-card border border-accent/40 bg-accent/10 px-4 py-3 text-sm text-ink"
        >
          Couldn&apos;t load messages: {error}{" "}
          <button
            type="button"
            onClick={() => void load(tab)}
            className="font-semibold text-primary underline-offset-2 hover:underline"
          >
            Retry
          </button>
        </div>
      )}

      {!loading && !error && rows.length === 0 && (
        <div className="rounded-card border border-border bg-card px-4 py-12 text-center">
          <Inbox className="mx-auto h-8 w-8 text-muted/60" aria-hidden />
          <p className="mt-3 text-sm text-muted">{EMPTY_COPY[tab]}</p>
        </div>
      )}

      {!loading && !error && rows.length > 0 && (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Received</TableHead>
                  <TableHead>From</TableHead>
                  <TableHead>Message</TableHead>
                  <TableHead>Topic</TableHead>
                  <TableHead className="text-right">Replies</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((m) => (
                  <TableRow
                    key={m.id}
                    className="cursor-pointer"
                    onClick={() =>
                      void navigate({
                        to: "/admin/messages/$id",
                        params: { id: String(m.id) },
                      })
                    }
                  >
                    <TableCell className="whitespace-nowrap text-muted">
                      {formatDay(m.created_at)}
                    </TableCell>
                    <TableCell>
                      <div className="font-medium text-ink">{m.name?.trim() || "Anonymous"}</div>
                      {(m.email || m.phone) && (
                        <div className="max-w-[12rem] truncate text-xs text-muted">
                          {m.email || m.phone}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="max-w-sm">
                      {/* The Link is the keyboard-accessible path into the
                          detail; the row onClick is a pointer convenience. */}
                      <Link
                        to="/admin/messages/$id"
                        params={{ id: String(m.id) }}
                        onClick={(e) => e.stopPropagation()}
                        className="block truncate font-medium text-ink hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                        title={m.subject?.trim() || m.body}
                      >
                        {snippet(m)}
                      </Link>
                    </TableCell>
                    <TableCell>
                      <TopicBadge topic={m.topic} />
                    </TableCell>
                    <TableCell className="text-right">
                      {m.reply_count > 0 ? (
                        <span className="inline-flex items-center gap-1 text-sm text-muted">
                          <MessageSquareReply className="h-4 w-4" aria-hidden />
                          {m.reply_count}
                        </span>
                      ) : (
                        <Badge variant="muted">—</Badge>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </section>
  );
}

function snippet(m: Message): string {
  const subject = m.subject?.trim();
  if (subject) return subject;
  const body = m.body.replace(/\s+/g, " ").trim();
  return body.length > 90 ? `${body.slice(0, 90)}…` : body;
}
